import { VENDOR_PRICE_UNIT_LABELS } from '../enums/vendor-price-unit.enum.js'
import type { OfferingPriceDto } from '../dto/vendor-profile.dto.js'
import {
  DIETARY_TAG_LABELS,
  FLORAL_PIECE_LABELS,
  PLANNING_TIER_LABELS,
  type EquipmentDetails,
  type OfferingServiceDetails,
} from '../input/vendor-offering.input.js'
import { dollarsFromCents } from './format.js'

/**
 * A package's price as it reads: "$1,200 per event", "From $85 per guest",
 * "$40 each", or "Price on request" when there is no price to show.
 */
export function offeringPriceLabel(price: OfferingPriceDto | null): string {
  if (price === null) return 'Price on request'
  return `${price.from ? 'From ' : ''}${dollarsFromCents(price.cents)} ${VENDOR_PRICE_UNIT_LABELS[price.unit]}`
}

const hoursText = (hours: number): string => `${String(hours)} ${hours === 1 ? 'hour' : 'hours'}`
const listText = (label: string, items: readonly string[]): string[] => (items.length === 0 ? [] : [`${label}: ${items.join(', ')}`])

function equipmentLines(details: EquipmentDetails): string[] {
  const times = [
    details.setupMinutes === null ? null : `Setup ${String(details.setupMinutes)} minutes`,
    details.teardownMinutes === null ? null : `Teardown ${String(details.teardownMinutes)} minutes`,
  ].filter((t): t is string => t !== null)
  return [...listText('Equipment', details.equipment), ...(times.length === 0 ? [] : [times.join(' · ')])]
}

/**
 * A package's own fields as lines to read: "Courses: Appetizers, Entrée",
 * "Set length: 4 hours". Fields nobody filled in are left out, so a package
 * with none has no lines.
 */
export function offeringDetailLines(value: OfferingServiceDetails): string[] {
  switch (value.service) {
    case 'catering': {
      const { courses, dietaryTags } = value.details
      return [
        ...listText('Courses', courses),
        ...listText('Dietary needs', dietaryTags.map((tag) => DIETARY_TAG_LABELS[tag])),
      ]
    }
    case 'photography': {
      const { hours, deliverables } = value.details
      return [...(hours === null ? [] : [`Coverage: ${hoursText(hours)}`]), ...listText('Includes', deliverables)]
    }
    case 'entertainment': {
      const { setLengthHours, equipment } = value.details
      return [...(setLengthHours === null ? [] : [`Set length: ${hoursText(setLengthHours)}`]), ...listText('Equipment', equipment)]
    }
    case 'floral':
      return value.details.piece === null ? [] : [FLORAL_PIECE_LABELS[value.details.piece]]
    case 'beauty': {
      const { services, trialIncluded, onSite } = value.details
      return [...listText('Services', services), ...(trialIncluded ? ['Trial included'] : []), ...(onSite ? ['Comes to the venue'] : [])]
    }
    case 'transportation': {
      const { vehicleTypes, seats, minimumHours } = value.details
      const sizes = [seats === null ? null : `Seats ${String(seats)}`, minimumHours === null ? null : `Minimum ${hoursText(minimumHours)}`].filter(
        (t): t is string => t !== null,
      )
      return [...listText('Vehicles', vehicleTypes), ...(sizes.length === 0 ? [] : [sizes.join(' · ')])]
    }
    case 'lighting':
    case 'av':
      return equipmentLines(value.details)
    case 'planning': {
      const { tier, monthsOfInvolvement } = value.details
      const parts = [tier === null ? null : PLANNING_TIER_LABELS[tier], monthsOfInvolvement === null ? null : `${String(monthsOfInvolvement)} months`].filter(
        (t): t is string => t !== null,
      )
      return parts.length === 0 ? [] : [parts.join(' · ')]
    }
    default: {
      const unexpected: never = value
      return unexpected
    }
  }
}
